<template>
  <v-card class="tracker-wrapper" variant="flat">
    <v-row no-gutters>
      <v-col cols="12" md="8" class="mx-auto">
        <v-card-title class="d-flex align-center ga-2">
          <span>Need help or have a request?</span>
          <v-spacer />
          <v-btn icon variant="text" @click.stop="closeEventHandler">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="py-0">
          <v-alert
            class="mb-4"
            type="info"
            variant="tonal"
            density="compact"
          >
            Have a suggestion, a feature request or found a bug? Let us know: we track every
            request and tell you when it has been addressed.
          </v-alert>
          <div class="tracker-wrapper-content">
            <v-skeleton-loader :loading="loadingItems" type="article">
              <div
                class="tracker-wrapper-content__body"
                :class="{
                  'tracker-wrapper-content_body--list-mode': isListMode,
                  'tracker-wrapper-content_body--form-mode': !isListMode,
                }"
              >
                <TrackerWrapperList
                  class="tracker-wrapper-common-item"
                  :class="{ active: isListMode }"
                  :items="items"
                  :vote-loading-keys="voteLoadingKeys"
                  @vote="voteEventHandler"
                  @click:new-request="changeViewModeEventHandler"
                />
              </div>
            </v-skeleton-loader>
            <TrackerWrapperForm
              class="tracker-wrapper-common-item"
              :class="{ active: !isListMode }"
              :save-loading="saveLoading"
              @change:view="changeViewModeEventHandler"
              @send="sendEventHandler"
            />
          </div>
        </v-card-text>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapMutations, mapState } from 'vuex';

import { ServiceFactory } from '@/services/ServiceFactory';
import { useNotifications } from '@/bundles/App/composables/useNotifications';

import TrackerWrapperForm from './TrackerWrapperForm';
import TrackerWrapperList from './TrackerWrapperList';

let _settingsFactory;
const getSettingsFactory = () => {
  if (!_settingsFactory) _settingsFactory = ServiceFactory.get('settings');
  return _settingsFactory;
};

export default {
  name: 'TrackerWrapper',

  components: {
    TrackerWrapperList,
    TrackerWrapperForm,
  },

  setup () {
    const { addSuccessNotification } = useNotifications();

    return { addSuccessNotification };
  },

  data: () => ({
    viewMode: 'list',
    items: [],
    loadingItems: false,
    saveLoading: false,
    voteLoadingKeys: [],
  }),

  computed: {
    ...mapState({
      userId: (state) => state.userId,
      token: (state) => state.idToken,
      activeAccountId: (state) => state.activeAccount?.id
    }),
    member: function () {
      return `Member/${this.userId}`;
    },
    isListMode: function () {
      return this.viewMode === 'list';
    },
    formattedPage () {
      return this.$route.path.replace(`/${this.activeAccountId}`, '');
    }
  },

  created: function () {
    this.$eventBus.$on('open-bug-report', this.changeViewModeEventHandler);
    this.fetchList();
  },

  beforeUnmount () {
    this.$eventBus.$off('open-bug-report', this.changeViewModeEventHandler)
  },

  methods: {
    ...mapMutations('Notifications', ['ADD_NOTIFICATION']),
    /**
     * Handler for close event
     * */
    closeEventHandler: function () {
      this.$eventBus.$emit('closeFeedbackForm');
    },
    /**
     * Handler for change view mode event
     * */
    changeViewModeEventHandler: function () {
      this.viewMode = this.viewMode === 'list' ? 'form' : 'list';
    },
    /**
     * Fetch bug list from API by component
     * */
    fetchList: async function () {
      try {
        this.loadingItems = true;

        const params = [{ name: 'section', value: this.formattedPage }];
        const { data } = await getSettingsFactory().getRequests({ token: this.token, params });

        this.items = [...data.result];
      } catch (error) {
        const notification = { ...error };
        this.ADD_NOTIFICATION(notification);
      } finally {
        this.loadingItems = false;
      }
    },
    /**
     * Handler for send event
     * @param {Object} payload
     * */
    sendEventHandler: async function (payload) {
      const tempRequest = Object.assign(
        {
          type: this.type,
          page: this.formattedPage,
          member: this.member,
          session_fyi_url:
            (window.SessionRecorder &&
              typeof window.SessionRecorder.getSessionUrl === 'function' &&
              window.SessionRecorder.getSessionUrl()) ||
            '',
        },
        payload
      );
      try {
        this.saveLoading = true;

        const { data: item } = await getSettingsFactory().sendFeedback({
          token: this.token,
          payload: tempRequest,
        });

        this.viewMode = 'list';

        this.addSuccessNotification('Thank you! <br>Your request has been successfully submitted and will be reviewed by our team shortly.');
        await this.voteEventHandler(item._key);
        await this.fetchList();
      } catch (error) {
        const notification = { ...error };
        this.ADD_NOTIFICATION(notification);
      } finally {
        this.saveLoading = false;
      }
    },
    /**
     * Handler for vote event
     * */
    voteEventHandler: async function (id) {
      try {
        this.voteLoadingKeys.push(id);
        const { data } = await getSettingsFactory().vote({ id, token: this.token });
        const tempRecord = data.record;

        this.items = this.items.map((item) => {
          if (item._key === tempRecord._key) {
            return tempRecord;
          }

          return item;
        });
      } catch (error) {
        const notification = { ...error };
        this.ADD_NOTIFICATION(notification);
      } finally {
        this.voteLoadingKeys = this.voteLoadingKeys.filter((key) => key !== id);
      }
    },
  },
};
</script>
